/**
 * useEstimates — the React binding for the estimation feature.
 *
 * Strict layering: pages call THIS hook; the hook calls {@link EstimateStore};
 * the store calls the api facade. Pages never import the store or the api
 * directly.
 *
 * Unlike tasks, estimates are NOT strictly room-scoped — an estimate may be
 * standalone or optionally linked to a room. This hook lists ALL non-deleted
 * estimates and exposes create/getById/remove; callers filter by room in the
 * UI when they want a room-scoped view.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { EstimateStore } from './estimateStore';
import type { Estimate, EstimateDraft, EstimateSummary } from './types';

export interface UseEstimatesResult {
  /** All non-deleted estimate summaries. */
  summaries: EstimateSummary[];
  loading: boolean;
  error: string | null;
  /** Re-fetches the list from the store. */
  refresh: () => Promise<void>;
  /** Loads a full estimate document by id (hydrates inputs from S3). */
  getById: (id: string) => Promise<Estimate | undefined>;
  /** Creates a new estimate and returns the saved document. */
  create: (draft: EstimateDraft) => Promise<Estimate>;
  /** Soft-deletes an estimate. */
  remove: (id: string) => Promise<void>;
}

export function useEstimates(): UseEstimatesResult {
  const api = useApi();
  const store = useMemo(() => new EstimateStore(api), [api]);

  const [summaries, setSummaries] = useState<EstimateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await store.listSummaries();
      // Newest-first by createdAt.
      rows.sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
      setSummaries(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load estimates.');
    } finally {
      setLoading(false);
    }
  }, [store]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const getById = useCallback((id: string) => store.get(id), [store]);

  const create = useCallback(
    async (draft: EstimateDraft) => {
      const saved = await store.create(draft);
      await refresh();
      return saved;
    },
    [store, refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await store.softDelete(id);
      await refresh();
    },
    [store, refresh],
  );

  return {
    summaries,
    loading,
    error,
    refresh,
    getById,
    create,
    remove,
  };
}
